import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card, message, Select, Transfer, Input, Button, Space, Popconfirm,
  Modal, Alert, Upload, Radio, Typography, Divider,
} from 'antd';
import {
  DeleteOutlined, ReloadOutlined, PlusOutlined, EditOutlined, SaveOutlined,
  DownloadOutlined, UploadOutlined, InfoCircleOutlined, AppstoreOutlined,
} from '@ant-design/icons';
import {
  getAllRoles,
  getPolicies,
  updateRolePolicies,
  createRole,
  deleteRole,
  getRolePolicies,
  updateRole,
  getUserPermissions,
  exportRolesAndPolicies,
  importRolesAndPolicies,
} from '../../actions/userActions';
import { readJsonFile } from '../../utils/readJsonFile';

const { Option } = Select;
const { Text } = Typography;

const getRoleName = (role) => {
  if (typeof role === 'string') return role;
  if (typeof role === 'object' && role !== null) {
    return role.detail || role.name || JSON.stringify(role);
  }
  return 'Unknown Role';
};

const RolePermissionManager = ({ currentRole, onOpenNavPolicies, drawerHeight, transferListHeight }) => {
  const dispatch = useDispatch();

  const [selectedRole, setSelectedRole]               = useState(currentRole);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible]   = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [newRoleName, setNewRoleName]                 = useState('');
  const [editedRoleName, setEditedRoleName]           = useState('');
  const [selectedPolicies, setSelectedPolicies]       = useState([]);
  const [importMode, setImportMode]                   = useState('Merge');
  const [importFile, setImportFile]                   = useState(null);

  const rolesState       = useSelector((s) => s.roles || {});
  const policiesState    = useSelector((s) => s.policies || {});
  const rolePoliciesState = useSelector((s) => s.rolePolicies || {});
  const roleUpdateState  = useSelector((s) => s.roleUpdate || {});
  const roleCreateState  = useSelector((s) => s.roleCreate || {});
  const roleDeleteState  = useSelector((s) => s.roleDelete || {});
  const exportState      = useSelector((s) => s.rolesPoliciesExport || {});
  const importState      = useSelector((s) => s.rolesPoliciesImport || {});

  const { roles = [], loading: rolesLoading = false, error: rolesError = null } = rolesState;
  const { policies = [], loading: policiesLoading = false, error: policiesError = null } = policiesState;
  const { policies: rolePolicies = [], loading: rolePoliciesLoading = false, error: rolePoliciesError = null } = rolePoliciesState;
  const { error: updateRoleError = null } = roleUpdateState;
  const { error: createRoleError = null } = roleCreateState;
  const { error: deleteRoleError = null } = roleDeleteState;

  useEffect(() => { dispatch(getAllRoles()); dispatch(getPolicies()); }, [dispatch]);
  useEffect(() => {
    const role = Array.isArray(currentRole) ? currentRole[0] : currentRole;
    setSelectedRole(role || null);
  }, [currentRole]);
  useEffect(() => { if (selectedRole) dispatch(getRolePolicies(selectedRole)); }, [selectedRole, dispatch]);
  useEffect(() => { if (Array.isArray(rolePolicies)) setSelectedPolicies(rolePolicies); }, [rolePolicies]);
  useEffect(() => {
    if (Array.isArray(roles) && roles.length > 0 && !selectedRole)
      setSelectedRole(getRoleName(roles[0]));
  }, [roles, selectedRole]);

  useEffect(() => {
    const errs = [rolesError, policiesError, rolePoliciesError, updateRoleError, createRoleError, deleteRoleError].filter(Boolean);
    errs.forEach((e) => message.error(e));
  }, [rolesError, policiesError, rolePoliciesError, updateRoleError, createRoleError, deleteRoleError]);

  /* ── role CRUD ── */
  const refreshRolesAndSelect = async () => {
    await dispatch(getAllRoles());
    const updated = rolesState.roles || [];
    if (updated.length > 0) setSelectedRole(getRoleName(updated[0]));
  };

  const handleSavePolicies = async () => {
    if (!selectedRole) return;
    try {
      await dispatch(updateRolePolicies(selectedRole, { policies: selectedPolicies }));
      message.success('Role policies updated successfully');
      await dispatch(getUserPermissions());
    } catch {
      message.error('Failed to update role policies');
    }
  };

  const handleCreateRole = async () => {
    await dispatch(createRole(newRoleName));
    setIsCreateModalVisible(false);
    setNewRoleName('');
    await refreshRolesAndSelect();
  };

  const handleEditRole = async () => {
    await dispatch(updateRole(selectedRole, editedRoleName));
    setIsEditModalVisible(false);
    setEditedRoleName('');
    await refreshRolesAndSelect();
  };

  const handleDeleteRole = async (roleName) => {
    await dispatch(deleteRole(roleName));
    if (selectedRole === roleName) setSelectedRole(null);
    await refreshRolesAndSelect();
  };

  const refreshPolicies = () => {
    dispatch(getPolicies());
    if (selectedRole) dispatch(getRolePolicies(selectedRole));
  };

  /* ── export ── */
  const handleExport = async () => {
    try {
      const data = await dispatch(exportRolesAndPolicies());
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `roles-policies-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      message.success('Exported successfully');
    } catch {
      message.error('Export failed');
    }
  };

  /* ── import ── */
  const resetImportModal = () => {
    setIsImportModalVisible(false);
    setImportFile(null);
    setImportMode('Merge');
  };

  const handleImport = async () => {
    try {
      if (!importFile) { message.error('Please select a JSON file'); return; }
      const importData = await readJsonFile(importFile);
      const result = await dispatch(importRolesAndPolicies(importData, importMode));
      if (result?.success !== false) {
        const parts = [];
        if (result.rolesCreated) parts.push(`${result.rolesCreated} created`);
        if (result.rolesUpdated) parts.push(`${result.rolesUpdated} updated`);
        if (result.rolesDeleted) parts.push(`${result.rolesDeleted} deleted`);
        message.success(`Import complete${parts.length ? `: ${parts.join(', ')}` : ''}`);
        resetImportModal();
        dispatch(getAllRoles());
        dispatch(getPolicies());
        if (selectedRole) dispatch(getRolePolicies(selectedRole));
      } else {
        message.error(result?.message || 'Import failed');
      }
    } catch (e) {
      message.error(e instanceof SyntaxError ? 'Invalid JSON file' : 'Import failed');
    }
  };

  const errors = [rolesError, policiesError, rolePoliciesError, updateRoleError, createRoleError, deleteRoleError].filter(Boolean);

  const listHeight = transferListHeight || 'calc(90vh - 240px)';

  const policyActionBar = (
    <Space>
      <Button
        icon={<ReloadOutlined />}
        onClick={refreshPolicies}
        disabled={!selectedRole}
        loading={rolePoliciesLoading}
      >
        Refresh
      </Button>
      <Button
        type="primary"
        icon={<SaveOutlined />}
        onClick={handleSavePolicies}
        disabled={!selectedRole}
      >
        Save Changes
      </Button>
    </Space>
  );

  return (
    <div style={{ height: drawerHeight || '90vh', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 12, flexShrink: 0 }}>
        <div style={{ marginLeft: 'auto' }}>{policyActionBar}</div>
      </div>
    <Card
      title="Role and Permission Management"
      loading={rolesLoading && roles.length === 0}
      style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, border: 'none', boxShadow: 'none' }}
      styles={{ body: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
    >
      {errors.length > 0 && (
        <Alert
          message="Error"
          description={errors.join('. ')}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* ── Toolbar ── */}
      <Space wrap style={{ marginBottom: 16, flexShrink: 0 }}>
        <Select
          style={{ width: 200 }}
          placeholder="Select a role"
          value={selectedRole}
          onChange={setSelectedRole}
        >
          {Array.isArray(roles) && roles.map((role, idx) => (
            <Option key={idx} value={getRoleName(role)}>
              <Space>
                {getRoleName(role)}
                <Popconfirm
                  title="Delete this role?"
                  onConfirm={() => handleDeleteRole(getRoleName(role))}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    icon={<DeleteOutlined />}
                    size="small"
                    danger
                    onClick={(e) => e.stopPropagation()}
                  />
                </Popconfirm>
              </Space>
            </Option>
          ))}
        </Select>

        <Button icon={<ReloadOutlined />} onClick={refreshRolesAndSelect} title="Refresh roles" />
        <Button icon={<PlusOutlined />} onClick={() => setIsCreateModalVisible(true)} title="Create role" />
        <Button
          icon={<EditOutlined />}
          onClick={() => { setEditedRoleName(selectedRole || ''); setIsEditModalVisible(true); }}
          disabled={!selectedRole}
          title="Rename role"
        />

        <Divider type="vertical" />

        <Button
          icon={<DownloadOutlined />}
          onClick={handleExport}
          loading={exportState.loading}
          title="Export all roles & policies as JSON"
        >
          Export
        </Button>
        <Button
          icon={<UploadOutlined />}
          onClick={() => setIsImportModalVisible(true)}
          loading={importState.loading}
          title="Import roles & policies from JSON"
        >
          Import
        </Button>
        {onOpenNavPolicies && (
          <Button
            icon={<AppstoreOutlined />}
            onClick={onOpenNavPolicies}
            title="Configure which policies gate sidebar items, settings cards, and pages"
          >
            Nav Policy Management
          </Button>
        )}
      </Space>

      {/* ── Policy transfer ── */}
      {selectedRole && (
        <Card
          title={`Permissions for ${selectedRole}`}
          loading={rolePoliciesLoading}
          size="small"
          style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
          styles={{ body: { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
        >
          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            message={
              <>
                <Text strong>Left:</Text> policies not assigned to this role.{' '}
                <Text strong>Right:</Text> permissions granted to users with this role.
                Move items between lists, then save.
              </>
            }
            style={{ marginBottom: 12, flexShrink: 0 }}
          />
          <div className="role-permission-transfer-wrapper" style={{ height: listHeight, minHeight: 320 }}>
            <Transfer
              className="role-permission-transfer"
              dataSource={Array.isArray(policies) ? policies.map((p) => ({ key: p, title: p })) : []}
              titles={['Not assigned', 'Permissions for this role']}
              showSearch
              filterOption={(input, option) => option.title.toLowerCase().includes(input.toLowerCase())}
              targetKeys={selectedPolicies}
              onChange={setSelectedPolicies}
              render={(item) => item.title}
              listStyle={{ width: '100%', height: '100%' }}
            />
          </div>
        </Card>
      )}

      {/* ── Create role modal ── */}
      <Modal
        title="Create New Role"
        open={isCreateModalVisible}
        onOk={handleCreateRole}
        onCancel={() => setIsCreateModalVisible(false)}
      >
        <Input
          placeholder="Role name"
          value={newRoleName}
          onChange={(e) => setNewRoleName(e.target.value)}
        />
      </Modal>

      {/* ── Edit role modal ── */}
      <Modal
        title="Rename Role"
        open={isEditModalVisible}
        onOk={handleEditRole}
        onCancel={() => setIsEditModalVisible(false)}
      >
        <Input
          placeholder="New role name"
          value={editedRoleName}
          onChange={(e) => setEditedRoleName(e.target.value)}
        />
      </Modal>

      {/* ── Import modal ── */}
      <Modal
        title="Import Roles & Policies"
        open={isImportModalVisible}
        onOk={handleImport}
        onCancel={resetImportModal}
        okText="Import"
        confirmLoading={importState.loading}
        okButtonProps={{ disabled: !importFile }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            message={
              <>
                <Text strong>Merge</Text> adds/updates roles without removing existing ones.{' '}
                <Text strong>Override</Text> replaces all non-protected roles.
                Protected roles (SuperUser, Admin, User) are never deleted.
              </>
            }
          />

          <div>
            <Text type="secondary">Import mode</Text>
            <br />
            <Radio.Group
              value={importMode}
              onChange={(e) => setImportMode(e.target.value)}
              style={{ marginTop: 8 }}
            >
              <Radio value="Merge">Merge</Radio>
              <Radio value="Override">Override</Radio>
            </Radio.Group>
          </div>

          <Upload
            accept=".json"
            maxCount={1}
            beforeUpload={(file) => { setImportFile(file); return false; }}
            onRemove={() => setImportFile(null)}
            fileList={importFile ? [{ uid: '1', name: importFile.name, status: 'done' }] : []}
          >
            <Button icon={<UploadOutlined />}>Select JSON file</Button>
          </Upload>
        </Space>
      </Modal>
    </Card>
    </div>
  );
};

export default RolePermissionManager;
